import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** The modifier keys read off a click event to decide the selection gesture. */
export interface MultiSelectMods {
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
}

export interface UseMultiSelectReturn {
  selectedIds: Set<string>;
  count: number;
  isSelected: (id: string) => boolean;
  /**
   * Handle a selection click on `id`:
   *  - plain      → select just this id and make it the anchor
   *  - shift      → select the contiguous range anchor…id (fresh; anchor kept so
   *                 re-ranging from the same anchor works), in visible order
   *  - cmd / ctrl → toggle this id in/out; it becomes the new anchor
   */
  handleSelectClick: (id: string, mods: MultiSelectMods) => void;
  /** Clear the whole selection (Clear button / Esc / click-empty). */
  clear: () => void;
  /** Drop any selected ids no longer present (call when the visible list changes). */
  retain: (validIds: Set<string>) => void;
}

/**
 * Range-aware multi-select over an ORDERED list of ids (the visible task order).
 * The order is read through a ref so `handleSelectClick` keeps a stable identity
 * while range math always uses the latest order. Selection is purely client-side
 * UI state — callers apply bulk actions over `selectedIds`.
 */
export function useMultiSelect(orderedIds: string[]): UseMultiSelectReturn {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const anchorRef = useRef<string | null>(null);
  const orderRef = useRef(orderedIds);
  orderRef.current = orderedIds;

  const handleSelectClick = useCallback((id: string, mods: MultiSelectMods) => {
    setSelectedIds((prev) => {
      const order = orderRef.current;
      // Shift: contiguous range from the anchor to the clicked id. Replaces the
      // selection with that range (standard list behaviour) and KEEPS the anchor
      // so a second shift-click re-ranges from the same origin.
      if (mods.shiftKey && anchorRef.current !== null) {
        const a = order.indexOf(anchorRef.current);
        const b = order.indexOf(id);
        if (a !== -1 && b !== -1) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          const next = new Set<string>();
          for (let i = lo; i <= hi; i++) next.add(order[i]);
          return next;
        }
      }
      // Cmd / Ctrl: toggle a single id; it becomes the anchor.
      if (mods.metaKey || mods.ctrlKey) {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        anchorRef.current = id;
        return next;
      }
      // Plain click: select just this id and anchor on it.
      anchorRef.current = id;
      return new Set([id]);
    });
  }, []);

  const clear = useCallback(() => {
    anchorRef.current = null;
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  const retain = useCallback((validIds: Set<string>) => {
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (validIds.has(id)) next.add(id);
        else changed = true;
      }
      if (anchorRef.current && !validIds.has(anchorRef.current)) anchorRef.current = null;
      return changed ? next : prev;
    });
  }, []);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  // Dismiss the selection on Esc or a click/tap that lands OFF the tasks — shared
  // by every surface using this hook (the List view and the My Tasks board) so
  // the two can't drift. The selection is kept when the click lands on a task
  // row, the selection indicator, or an open menu/dialog (whose actions operate
  // ON the selection). Capture-phase + document-level so it fires regardless of
  // stopPropagation, anywhere on screen. Only armed while something is selected.
  const count = selectedIds.size;
  useEffect(() => {
    if (count === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clear();
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (t.closest('[data-todo-row], [data-selection-indicator]')) return;
      if (
        document.querySelector(
          '[data-radix-popper-content-wrapper], [role="dialog"], [role="alertdialog"]',
        )
      ) {
        return;
      }
      clear();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [count, clear]);

  return useMemo(
    () => ({ selectedIds, count: selectedIds.size, isSelected, handleSelectClick, clear, retain }),
    [selectedIds, isSelected, handleSelectClick, clear, retain],
  );
}
